import { default as RCPagination } from 'rc-pagination';
import type { PaginationProps as RCPaginationProps } from 'rc-pagination';
import styled from 'styled-components';

import { styledComponentsTheme } from '@frontend/legacy-components';
import {
  EllipsisHorizontalIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from '@frontend/legacy-components/src/components/mediaAndIcons/icons/iconSet';
import { noop } from '@frontend/utils';

// Currently in-use by the table component as its built-in pagination.
const StyledRCPagination = styled(RCPagination)`
  align-items: center;
  display: flex;
  color: ${(props) => props.theme.color.feature.primary};
  flex-direction: horizontal;
  font-family: ${(props) => props.theme.font.family.default};
  ${(props) => props.theme.font.size.E};
  font-weight: ${(props) => props.theme.font.weight.medium};
  justify-content: center;
  margin: 0;
  padding: 0;
  user-select: none;
  width: fit-content;

  ul,
  ol {
    list-style: none;
    margin: 0;
    padding: 0;
  }

  .rc-pagination-item {
    background-color: ${(props) => props.theme.color.page.surface};
    border: 1px solid ${(props) => props.theme.color.page.surface};
    border-radius: 0.125rem;
    cursor: pointer;
    display: inline-block;
    height: 1.5rem;
    list-style: none;
    margin-right: 0.25rem;
    ${(props) => props.theme.font.size.E};
    min-width: 1.5rem;
    outline: none;
    text-align: center;
    user-select: none;
    vertical-align: middle;

    a {
      display: block;
      margin-top: -1px; /*this ensures wrapping border fits appropriately */
      transition: none;

      :hover {
        text-decoration: none;
      }
    }

    :focus {
      box-shadow: 0 0 0 3px ${(props) => props.theme.color.interaction.focus};
    }
    :hover {
      border: 1px solid ${(props) => props.theme.color.interaction.hover};

      a {
        color: ${(props) => props.theme.color.interaction.hover};
      }
    }
  }

  .rc-pagination-item-active {
    background-color: ${(props) => props.theme.color.page.surface};
    border: 1px solid ${(props) => props.theme.color.interaction.main};

    a {
      color: ${(props) => props.theme.color.interaction.main};
    }
  }

  .rc-pagination-jump-prev,
  .rc-pagination-jump-next,
  .rc-pagination-prev,
  .rc-pagination-next {
    align-items: center;
    border-radius: 0.125rem;
    color: ${(props) => props.theme.color.feature.secondary};
    cursor: pointer;
    display: inline-flex;
    height: 1.5rem;
    justify-content: center;
    margin-right: 0.25rem;
    min-width: 1.5rem;
    outline: 0;

    :hover:not(.rc-pagination-disabled) {
      color: ${(props) => props.theme.color.interaction.hover};
    }

    :active:not(.rc-pagination-disabled) {
      color: ${(props) => props.theme.color.interaction.active};
    }

    :focus:not(.rc-pagination-disabled) {
      box-shadow: 0 0 0 3px ${(props) => props.theme.color.interaction.focus};
    }
  }

  .rc-pagination-next {
    margin-right: 0;
  }

  .rc-pagination-jump-prev.rc-pagination-disabled,
  .rc-pagination-jump-next.rc-pagination-disabled,
  .rc-pagination-prev.rc-pagination-disabled,
  .rc-pagination-next.rc-pagination-disabled {
    color: ${(props) => props.theme.color.interaction.disabled};
  }
  .rc-pagination-disabled,
  .rc-pagination-disabled:hover,
  .rc-pagination-disabled:focus {
    cursor: not-allowed;
  }
`;

export type PaginationProps = {
  totalItems: number;
  itemsPerPage: number;
  currentPage: number;
  onChangePage: RCPaginationProps['onChange'];
} & Omit<
  RCPaginationProps,
  | 'showSizeChanger'
  | 'pageSizeOptions'
  | 'hideOnSinglePage'
  | 'showPrevNextJumpers'
  | 'showQuickJumper'
  | 'simple'
  | 'showLessItems'
  | 'showTitle'
>;
export const Pagination = ({
  totalItems,
  itemsPerPage = 10,
  currentPage = 1,
  onChangePage = noop,
  ...restProps
}: PaginationProps) => {
  return (
    <StyledRCPagination
      total={totalItems}
      pageSize={itemsPerPage}
      defaultPageSize={10}
      current={currentPage}
      onChange={(current, pageSize) => onChangePage(current, pageSize)}
      jumpPrevIcon={<EllipsisHorizontalIcon size={2.5} />}
      jumpNextIcon={<EllipsisHorizontalIcon size={2.5} />}
      prevIcon={<ChevronLeftIcon size={2.5} />}
      nextIcon={<ChevronRightIcon size={2.5} />}
      {...restProps}
      theme={styledComponentsTheme}
      showSizeChanger={false}
      hideOnSinglePage={false}
      showPrevNextJumpers
      showQuickJumper={false}
      showLessItems={false}
      showTitle={false}
    />
  );
};
